/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import React from 'react';
import type { Source } from '@opengeoweb/shared';
import {
  ToolContainerDraggable,
  useWheelStopPropagation,
} from '@opengeoweb/shared';
import type { LayerFoundation } from '@opengeoweb/webmap';
import { Legend } from './Legend';
import { useWebmapReactTranslation } from '../../utils/i18n';

interface LegendDialogProps {
  layers: LayerFoundation[];
  isOpen: boolean;
  onClose: () => void;
  onMouseDown?: () => void;
  mapId: string;
  showMapId?: boolean;
  order?: number;
  source?: Source;
  onToggleLayerLegendVisibility: (layerId: string, showLegend: boolean) => void;
}

export const LegendDialog: React.FC<LegendDialogProps> = ({
  layers,
  isOpen,
  onClose,
  onMouseDown = (): void => {},
  mapId,
  showMapId = false,
  order = 0,
  source = 'app',
  onToggleLayerLegendVisibility,
}: LegendDialogProps) => {
  const { t } = useWebmapReactTranslation();

  const stopWheelEventPropagationRef =
    useWheelStopPropagation<HTMLDivElement>();

  return (
    <ToolContainerDraggable
      bounds="parent"
      data-testid="moveable-legend"
      initialMaxHeight={440}
      isOpen={isOpen}
      minWidth={100}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={{ right: 20, top: 50 }}
      title={
        showMapId
          ? `${t('webmap-react-legend')} ${mapId}`
          : t('webmap-react-legend')
      }
    >
      <Box
        sx={{
          padding: 0.5,
        }}
      >
        {layers && layers.length > 0 ? (
          <Box
            data-testid="LegendList"
            ref={stopWheelEventPropagationRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              padding: '0 16px',
              '&>div:last-child': {
                marginBottom: 0,
              },
              '&>div:first-child': {
                marginTop: '8px',
              },
            }}
          >
            {layers.map((layer) => (
              <Legend
                key={layer.id}
                layer={layer}
                onToggleLayerLegendVisibility={onToggleLayerLegendVisibility}
              />
            ))}
          </Box>
        ) : (
          <Typography
            data-testid="NoLayers"
            style={{ fontWeight: 500, fontSize: 14 }}
            variant="body1"
          >
            {t('webmap-react-no-layers')}
          </Typography>
        )}
      </Box>
    </ToolContainerDraggable>
  );
};
